'use client';

import type { GridState } from 'ag-grid-community';

/**
 * Utilities for managing table persistence across the application
 */

/**
 * Standard table IDs for consistent naming across the application
 */
export const TABLE_IDS = {
  // Main pages
  FINDINGS: 'findings',
  REPORTS: 'reports',

  // Application-specific tables
  APPLICATION_FINDINGS: 'application_findings',
  APPLICATION_EXECUTIONS: 'application_executions',

  // Network findings tables
  EXTERNAL_NETWORK_FINDINGS: 'external_network_findings',
  INTERNAL_NETWORK_FINDINGS: 'internal_network_findings',

  // Terra Portal tables
  PROXY_GLOBAL_REQUESTS: 'proxy_global_requests',
  PROXY_HTTP_HISTORY: 'proxy_http_history',
  PROXY_EXECUTIONS: 'proxy_executions',
  WEBSOCKET_CONNECTIONS: 'websocket_connections',
  WEBSOCKET_MESSAGES: 'websocket_messages',
  PROXY_SIGNALS_LIST: 'proxy_signals_list_v2',
  PROXY_REJECTED_SIGNALS_LIST: 'proxy_rejected_signals_list',
  PROXY_FINDINGS_LIST: 'proxy_findings_list',
  PROXY_ENDPOINT_ASSETS_LIST: 'proxy_endpoint_assets_list',
  PROXY_WEB_PAGE_ASSETS_LIST: 'proxy_web_page_assets_list',
  PROXY_UNVERIFIED_ASSETS_LIST: 'proxy_unverified_assets_list',
  PROXY_EXECUTIONS_LIST: 'proxy_executions_list_v3',
  PROXY_AGENT_EXECUTIONS_LIST: 'proxy_agent_executions_list',
  PROXY_REPORTS_LIST: 'proxy_reports_list',

  // Terra Extension tables
  EXT_EXECUTIONS: 'ext_executions',
} as const;

export type ExternalFilters = Record<string, unknown>;

export type TableState = {
  data: GridState;
  externalFilters?: ExternalFilters;
  timestamp?: number;
};

export type TableId = (typeof TABLE_IDS)[keyof typeof TABLE_IDS];

/**
 * Shared constants for table persistence
 */
export const TABLE_STATE_PREFIX = 'terra_';

/**
 * Get timestamp for x days ago
 */
export function getXDaysAgoInMs(x: number): number {
  const daysInMs = x * 24 * 60 * 60 * 1000;

  return Date.now() - daysInMs;
}

/**
 * Generate a table suffix for application-specific tables
 */
export function createApplicationTableSuffix(applicationId: string): string {
  return `app_${applicationId}`;
}

/**
 * Generate a table suffix for user-specific variations
 * Creates a safe identifier from user ID by hashing sensitive info
 */
export function createUserTableSuffix(userId: string): string {
  // Create a simple hash of the user ID to avoid storing sensitive info in localStorage keys
  const hash = Array.from(userId).reduce((hash, char) => {
    const charCode = char.charCodeAt(0);

    return ((hash << 5) - hash + charCode) & 0xffffffff; // Keep it 32-bit
  }, 0);

  // Convert to positive number and use absolute value for consistency
  const hashString = Math.abs(hash).toString(36);

  return `user_${hashString}`;
}

// Generate checksum for data integrity
export const generateChecksum = (data: TableState): string => {
  try {
    const dataStr = JSON.stringify(data);

    // Simple but effective checksum using a salt
    const salt = 'terra_table_persistence_salt_v1';
    let hash = 0;
    const combined = dataStr + salt;

    for (let i = 0; i < combined.length; i++) {
      const char = combined.charCodeAt(i);

      hash = (hash << 5) - hash + char;
      hash = hash & hash; // Convert to 32-bit integer
    }

    return hash.toString(36);
  } catch (error) {
    console.warn('Failed to generate checksum:', error);

    return 'invalid';
  }
};

export type PersistenceConfigProps = {
  tableId: TableId;
  applicationId?: string;
  externalFilters?: ExternalFilters;
  onExternalFiltersRestore?: (filters: ExternalFilters) => void;
  onPersistenceReset?: () => void;
};

export type PersistenceConfig = PersistenceConfigProps & {
  userId: string | undefined;
};
